import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/24/solid'
import { useState, useEffect } from 'react';
import _ from 'lodash';
import {useClassNames} from '../../hooks/useClassNames';
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../translations/translationsConstants";

export default function Pagination({ pagination, onPaginationChange, disabled, showPageIndex }) {
    const { classNames } = useClassNames();
    const [internalPagination, setInternalPagination] = useState(pagination)
    const { t: tc } = useTranslation([resources.common]);

    useEffect(() => {
        setPaginationPage(pagination.currentPage);
    }, [pagination.totalItems, pagination.currentPage])


    const setPaginationPage = (newPageIdx) => {
        let currentPage = newPageIdx;
        const { pageSize, maxPages, totalItems } = pagination;
        const totalPages = Math.ceil(totalItems / pageSize);

        if (currentPage < 0) {
            currentPage = 0;
        } else if (currentPage > totalPages) {
            currentPage = totalPages;
        }

        let startPage = currentPage === 0 ? 0 : pagination.pages[0];
        let endPage = currentPage === 0 ? totalPages : pagination.pages[pagination.pages.length - 1];
        if (totalPages <= maxPages) {
            startPage = 0;
            endPage = totalPages;
        } else {
            let maxPagesBeforeCurrentPage = Math.floor(maxPages / 2) - 1;
            let maxPagesAfterCurrentPage = Math.ceil(maxPages / 2);
            if (currentPage <= maxPagesAfterCurrentPage) {
                startPage = 0;
                endPage = maxPages;
            } else if ((currentPage + maxPagesAfterCurrentPage) >= totalPages) {
                startPage = totalPages - maxPages;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxPagesBeforeCurrentPage;
                endPage = currentPage + maxPagesAfterCurrentPage;
            }
        }
        if (startPage <= 0) {
            startPage = 0;
            endPage = (totalPages <= maxPages) ? totalPages : maxPages;
        } else if (endPage > totalPages) {
            endPage = totalPages;
            startPage = totalPages - maxPages;
        }

        const pages = _.range(startPage, endPage, 1);

        const startIndex = currentPage * pageSize;

        const newPagination = {
            ...pagination,
            currentPage: currentPage,
            pages: pages,
            pageSize: pageSize,
            maxPages: maxPages,
            totalItems: totalItems,
            totalPages: totalPages,
            startIndex: startIndex,
        };
        setInternalPagination(newPagination);

        return newPagination;
    }

    const handlePreviousPageClick = () => {
        const newPagination = setPaginationPage((internalPagination.currentPage - 1));
        onPaginationChange(newPagination);
    }

    const handleNextPageClick = () => {
        const newPagination = setPaginationPage((internalPagination.currentPage + 1));
        onPaginationChange(newPagination);
    }

    const handlePageIndexClick = (pageIndex) => {
        const newPagination = setPaginationPage(pageIndex);
        onPaginationChange(newPagination);
    }

    return (
        <nav className="tw-w-full tw-px-4 tw-flex tw-items-center tw-justify-between sm:tw-px-0">
            <div className="tw--mt-px tw-w-0 tw-flex-1 tw-flex">
                <button
                    type="button"
                    className="tw-bg-transparent tw-border-0 tw-pt-4 tw-pr-1 tw-inline-flex tw-items-center tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-indigo-700 disabled:tw-cursor-not-allowed disabled:tw-text-gray-500"
                    disabled={disabled || (internalPagination.currentPage === 0)}
                    onClick={handlePreviousPageClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handlePreviousPageClick();
                        }
                    }}
                >
                    <ArrowLongLeftIcon className="tw-mr-3 tw-h-5 tw-w-5 tw-text-gray-400" aria-hidden="true" />
                    {tc(translations.common.previous)}
                </button>
            </div>
            {showPageIndex && (
                <div className="tw-hidden md:tw--mt-px md:tw-flex">
                    {internalPagination?.pages && internalPagination.pages.map((pageIndex) => (
                        <button
                            key={pageIndex}
                            type="button"
                            className={classNames(
                                "tw-bg-transparent tw-border-0 tw-pt-4 tw-px-4 tw-inline-flex tw-items-center tw-text-sm tw-font-medium disabled:tw-cursor-not-allowed disabled:tw-text-gray-500",
                                (internalPagination.currentPage === pageIndex)
                                    ?
                                    "tw-text-indigo-700"
                                    :
                                    "tw-text-gray-500 hover:tw-text-gray-700 "
                            )}
                            disabled={disabled}
                            onClick={() => handlePageIndexClick(pageIndex)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handlePageIndexClick(pageIndex);
                                }
                            }}
                        >
                            {pageIndex + 1}
                        </button>
                    ))}
                </div>
            )}
            <div className="tw--mt-px tw-w-0 tw-flex-1 tw-flex tw-justify-end">
                <button
                    type="button"
                    className="tw-bg-transparent tw-border-0 tw-pt-4 tw-pl-1 tw-inline-flex tw-items-center tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-indigo-700 disabled:tw-cursor-not-allowed disabled:tw-text-gray-500"
                    disabled={disabled || (internalPagination.currentPage === internalPagination.pages[internalPagination.pages.length - 1])}
                    onClick={handleNextPageClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleNextPageClick();
                        }
                    }}
                >
                    {tc(translations.common.next)}
                    <ArrowLongRightIcon className="tw-ml-3 tw-h-5 tw-w-5 tw-text-gray-400" aria-hidden="true" />
                </button>
            </div>
        </nav>
    )
}
